import PropTypes, { type Requireable } from 'prop-types'
import { type Chamber } from 'components'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'

export const suffixOptions = [
  { id: 0, value: ' ' },
  { id: 1, value: 'Jr.' },
  { id: 2, value: 'Sr.' },
  { id: 3, value: 'II' },
  { id: 4, value: 'III' },
  { id: 5, value: 'IV' }
]

export const genderOptions = [
  { id: 0, code: 'O', value: 'Unknown' },
  { id: 1, code: 'M', value: 'Male' },
  { id: 2, code: 'F', value: 'Female' }
]

export const PREFIX_IDS = {
  MR: 1,
  MRS: 2,
  MS: 3,
  MISS: 4
} as const

export const GENDER_IDS = {
  UNKNOWN: 0,
  MALE: 1,
  FEMALE: 2
} as const

export const getGenderFromPrefix = (prefixId: number | null): number | null => {
  if (prefixId === null || prefixId === undefined) {
    return null
  }

  switch (prefixId) {
    case PREFIX_IDS.MR:
      return GENDER_IDS.MALE
    case PREFIX_IDS.MRS:
    case PREFIX_IDS.MS:
    case PREFIX_IDS.MISS:
      return GENDER_IDS.FEMALE
    default:
      return null
  }
}

export const ethnicityOptions = [
  { id: 0, value: '' },
  { id: 1, value: 'Black/African American' },
  { id: 2, value: 'Hispanic/Latino' },
  { id: 3, value: 'Indian/Native American' },
  { id: 4, value: 'Asian/Pacific American' },
  { id: 5, value: 'White/Caucasian' },
  { id: 6, value: 'Two or More Ethnicities' },
  { id: 7, value: 'Hawaiian/Pacific Islander' },
  { id: 8, value: 'Other' }

]

export const maritalStatusOptions =
    [
      { id: 0, value: '' },
      { id: 1, value: 'Married' },
      { id: 2, value: 'Separated' },
      { id: 3, value: 'Divorced' },
      { id: 4, value: 'Widow' },
      { id: 5, value: 'Domestic Partner' },
      { id: 6, value: 'Significant Other' },
      { id: 7, value: 'Life Partner' },
      { id: 8, value: 'Single' },
      { id: 9, value: 'Not Stated' },
      { id: 12, value: 'Engaged' },
      { id: 13, value: 'Widower' }
    ]

// Define a mapping of TypeScript types to PropTypes
interface TypeToPropTypeMap {
  string: typeof PropTypes.string
  number: typeof PropTypes.number
  boolean: typeof PropTypes.bool
  object: typeof PropTypes.object
  array: typeof PropTypes.array
  any: typeof PropTypes.any
}

const typeToPropTypeMap: TypeToPropTypeMap = {
  string: PropTypes.string,
  number: PropTypes.number,
  boolean: PropTypes.bool,
  object: PropTypes.object,
  array: PropTypes.array,
  any: PropTypes.any
}

const typeToPropType = (type: keyof TypeToPropTypeMap): Requireable<any> => {
  return typeToPropTypeMap[type]
}

export const createPropTypesFromInterface = (interfaceObject: Record<string, keyof TypeToPropTypeMap>): Record<string, any> => {
  const propTypes: Record<string, any> = {}
  for (const key in interfaceObject) {
    if (Object.prototype.hasOwnProperty.call(interfaceObject, key)) {
      propTypes[key] = typeToPropType(interfaceObject[key])
    }
  }
  return propTypes
}
export const religionOptions =
  [
    { id: 0, code: '', value: '' },
    { id: 1, code: 'OTH', value: 'Unspecified/Other' },
    { id: 2, code: 'AME', value: 'African Methodist Episcopal' },
    { id: 3, code: 'APO', value: 'Apostolic Christian' },
    { id: 4, code: 'ASG', value: 'Assembly of God' },
    { id: 5, code: 'BAP', value: 'Baptist' },
    { id: 6, code: 'CAT', value: 'Catholic' },
    { id: 7, code: 'BUD', value: 'Buddhism' },
    { id: 8, code: 'CHC', value: 'Christian Church' },
    { id: 9, code: 'CRC', value: 'Christian Reformed Church' },
    { id: 10, code: 'CSC', value: 'Christian Scientist' },
    { id: 11, code: 'COC', value: 'Church of Christ' },
    { id: 12, code: 'CON', value: 'Congregationalist' },
    { id: 13, code: 'DOC', value: 'Disciples of Christ' },
    { id: 14, code: 'EOR', value: 'Eastern Orthodox' },
    { id: 15, code: 'EPI', value: 'Episcopalian' },
    { id: 16, code: 'FRH', value: 'French Huguenot' },
    { id: 17, code: 'FMC', value: 'Friedensthal Moravian Church' },
    { id: 18, code: 'GRO', value: 'Greek Orthodox' },
    { id: 19, code: 'HIN', value: 'Hinduism' },
    { id: 20, code: 'USC', value: 'Independent Bible Church' },
    { id: 21, code: 'JEW', value: 'Jewish' },
    { id: 22, code: 'ISL', value: 'Islam (Muslim)' },
    { id: 23, code: 'LUT', value: 'Lutheran' },
    { id: 24, code: 'MET', value: 'Methodist' },
    { id: 25, code: 'MOR', value: 'Mormon' },
    { id: 26, code: 'NAZ', value: 'Nazarene' },
    { id: 27, code: 'PEN', value: 'Pentecostal' },
    { id: 28, code: 'PRE', value: 'Presbyterian' },
    { id: 29, code: 'USP', value: 'Protestant - Unspecified Christian' },
    { id: 30, code: 'QKR', value: 'Quaker' },
    { id: 31, code: 'LDS', value: 'Reorganized Church of Jesus Christ of Latter Day Saints' },
    { id: 32, code: 'ROC', value: 'Roman Catholic' },
    { id: 33, code: 'SDA', value: 'Seventh-Day Adventist' },
    { id: 34, code: 'UNI', value: 'Unitarian' },
    { id: 35, code: 'UNT', value: 'United Church of Christ' },
    { id: 37, code: 'COB', value: 'Church of the Brethren' },
    { id: 38, code: 'WES', value: 'Wesleyan' },
    { id: 39, code: 'MEN', value: 'Mennonite' },
    { id: 40, code: 'FOU', value: 'Foursquare' },
    { id: 41, code: 'SHE', value: 'Shekinah Fellowship' },
    { id: 42, code: 'EVA', value: 'Evangelical' },
    { id: 43, code: 'ANG', value: 'Anglican' },
    { id: 44, code: 'ATH', value: 'Atheist' },
    { id: 45, code: 'BRE', value: 'Brethren Church' },
    { id: 46, code: 'SBP', value: 'Southern Baptist' },
    { id: 47, code: 'CMC', value: 'Community of Christ' },
    { id: 48, code: 'ACT', value: 'Anglican Catholic' },
    { id: 49, code: 'NON', value: 'None' },
    { id: 50, code: 'COG', value: 'Church of God' },
    { id: 51, code: 'CND', value: 'Christian - Non-Denominational' },
    { id: 52, code: 'CUB', value: 'Church of the United Brethren in Christ' },
    { id: 53, code: 'CJC', value: 'Church of Jesus Christ of Latter Day Saints' }
  ]

export const URL_DESC_TYPES = {
  EMAIL: 1,
  EMAIL_WEBFORM: 2,
  WEBSITE: 3,
  WEBLOG: 4,
  IMAGE_LOGO: 5,
  FACEBOOK: 6,
  INSTAGRAM: 7,
  LINKEDIN: 8,
  TWITTER: 9,
  YOUTUBE: 10,
  WIKIPEDIA: 11,
  SOURCE: 12
}

export const URL_DESC_TYPES_REVERSE = Object.fromEntries(
  Object.entries(URL_DESC_TYPES).map(([key, value]) => [value, key])
) as Record<number, keyof typeof URL_DESC_TYPES>

export interface UrlType {
  id: number
  desc: string
  lutOrder: number
  updatedBy: string
  lastUpdated: string
}
export const URL_TYPES: Record<string, UrlType> = {
  EMAIL: { id: 1, desc: 'Email', lutOrder: 1, updatedBy: 'TAD', lastUpdated: '2004-12-08T08:00:00' },
  WEBSITE: { id: 2, desc: 'Website', lutOrder: 2, updatedBy: 'TAD', lastUpdated: '2004-12-08T08:00:00' }
} as const

export const US_COMMITTEE_ROLES = {
  FED_COMMITTEE: 3,
  FED_SUBCOMMITTEE: 37,
  STATE_COMMITTEE: 4,
  STATE_SUBCOMMITTEE: 38,
  FED_CAUCUS: 39,
  STATE_CAUCUS: 43
}

export const US_LEGISLATIVE_MEMBER_ROLES = {
  FED_LEGISLATIVE_MEMBER: 1,
  STATE_LEGISLATIVE_MEMBER: 2
}

const COMMITTEE_URL_TYPES_WITHOUT_WEBFORM = [
  URL_DESC_TYPES.EMAIL,
  URL_DESC_TYPES.WEBSITE,
  URL_DESC_TYPES.FACEBOOK,
  URL_DESC_TYPES.INSTAGRAM,
  URL_DESC_TYPES.LINKEDIN,
  URL_DESC_TYPES.TWITTER,
  URL_DESC_TYPES.YOUTUBE
] as const

const COMMITTEE_URL_TYPES_WITH_WEBFORM = [
  URL_DESC_TYPES.EMAIL,
  URL_DESC_TYPES.EMAIL_WEBFORM,
  URL_DESC_TYPES.WEBSITE,
  URL_DESC_TYPES.FACEBOOK,
  URL_DESC_TYPES.INSTAGRAM,
  URL_DESC_TYPES.LINKEDIN,
  URL_DESC_TYPES.TWITTER,
  URL_DESC_TYPES.YOUTUBE
] as const

export const COMMITTEE_ROLE_URL_MAPPINGS = {
  [US_COMMITTEE_ROLES.FED_COMMITTEE]: COMMITTEE_URL_TYPES_WITH_WEBFORM,
  [US_COMMITTEE_ROLES.STATE_COMMITTEE]: COMMITTEE_URL_TYPES_WITH_WEBFORM,
  [US_COMMITTEE_ROLES.FED_SUBCOMMITTEE]: COMMITTEE_URL_TYPES_WITHOUT_WEBFORM,
  [US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE]: COMMITTEE_URL_TYPES_WITHOUT_WEBFORM,
  [US_COMMITTEE_ROLES.FED_CAUCUS]: COMMITTEE_URL_TYPES_WITHOUT_WEBFORM,
  [US_COMMITTEE_ROLES.STATE_CAUCUS]: COMMITTEE_URL_TYPES_WITHOUT_WEBFORM
} as const

export const US_ELECT_ROLES = {
  FED_LEGISLATOR_ELECT: 52,
  STATE_LEGISLATOR_ELECT: 54,
  FED_LEGISLATOR_ELECT_STAFF: 56,
  STATE_LEGISLATOR_ELECT_STAFF: 58,
  FED_EXECUTIVE_ELECT: 59,
  FED_EXECUTIVE_ELECT_STAFF: 60,
  STATE_EXECUTIVE_ELECT: 63,
  STATE_EXECUTIVE_ELECT_STAFF: 64
}

export const US_CANDIDATE_ROLES = {
  FED_LEGISLATOR_CANDIDATE: 51,
  STATE_LEGISLATOR_CANDIDATE: 53,
  FED_LEGISLATOR_CANDIDATE_STAFF: 55,
  STATE_LEGISLATOR_CANDIDATE_STAFF: 57,
  FED_EXECUTIVE_CANDIDATE: 61,
  FED_EXECUTIVE_CANDIDATE_STAFF: 62,
  STATE_EXECUTIVE_CANDIDATE: 65,
  STATE_EXECUTIVE_CANDIDATE_STAFF: 66
}

export const PHONE_TYPES = {
  PHONE: 1,
  FAX: 2,
  TTY_TDD: 3,
  GENERAL_INFORMATION: 4,
  RECORDER_INFORMATION: 5
} as const

export const US_COMMITTEE_MEMBER_ROLES = {
  FED_COMMITTEE_MEMBER: 13,
  FED_COMMITTEE_OFFICER: 14,
  STATE_COMMITTEE_MEMBER: 15,
  STATE_COMMITTEE_OFFICER: 16,
  FED_CAUCUS_MEMBER: 41,
  FED_CAUCUS_OFFICER: 40,
  STATE_CAUCUS_MEMBER: 45,
  STATE_CAUCUS_OFFICER: 44
}

export const US_COMMITTEE_MEMBER_LEG_ROLES = {
  FED_COMMITTEE_MEMBER: 171,
  FED_CAUCUS_MEMBER: 232,
  STATE_COMMITTEE_MEMBER: 172,
  STATE_CAUCUS_MEMBER: 311
}

export const US_LEADERSHIP_OFFICER_ROLES = {
  FEDERAL_OFFICER: 5,
  STATE_OFFICER: 6,
  FEDERAL_LEADERSHIP: 47,
  STATE_LEADERSHIP: 49
}

export const US_STAFFER_ROLES = {
  FEDERAL_OFFICER_STAFF: 11,
  STATE_OFFICER_STAFF: 12,
  FEDERAL_LEADERSHIP_STAFF: 48,
  STATE_LEADERSHIP_STAFF: 50,
  FEDERAL_COMMITTE_STAFF: 9,
  FEDERAL_CAUCUS_STAFF: 42,
  STATE_COMMITTE_STAFF: 10,
  STATE_CAUCUS_STAFF: 46,
  FEDERAL_MEMBER_STAFF: 7,
  STATE_MEMBER_STAFF: 8
}

export const US_GENERAL_BRANCHES = {
  FEDERAL_EXECUTIVE_BRANCH: 1,
  FEDERAL_LEGISLATIVE_BRANCH: 2,
  FEDERAL_JUDICIAL_BRANCH: 3,
  STATE_EXECUTIVE_BRANCH: 15,
  STATE_LEGISLATIVE_BRANCH: 16,
  STATE_JUDICIAL_BRANCH: 17
}

export const US_ADDITIONAL_ENTITY_ROLES = {
  FED_EXEC_OFFICE: 17,
  FED_EXEC_DEPARTMENT: 18,
  FED_EXEC_AGENCY: 19,
  STATE_EXEC_OFFICE: 20,
  STATE_EXEC_DEPARTMENT: 21,
  STATE_EXEC_AGENCY: 22,
  FED_LEG_OFFICE: 23,
  STATE_LEG_OFFICE: 24,
  FED_JUD_OFFICE: 25,
  STATE_JUD_OFFICE: 26,
  FED_JUD_OFFICE_STAFF: 28,
  FED_EXEC_OFFICE_STAFF: 27,
  FED_EXEC_DEPARTMENT_STAFF: 30,
  FED_EXEC_AGENCY_STAFF: 31,
  STATE_JUD_OFFICE_STAFF: 33,
  STATE_EXEC_OFFICE_STAFF: 32,
  STATE_EXEC_DEPARTMENT_STAFF: 35,
  STATE_EXEC_AGENCY_STAFF: 36,
  FED_PANEL: 67,
  FED_PANEL_OFFICER: 68,
  FED_PANEL_MEMBER: 69,
  FED_PANEL_STAFF: 70,
  STATE_PANEL: 71,
  STATE_PANEL_OFFICER: 72,
  STATE_PANEL_MEMBER: 73,
  STATE_PANEL_STAFF: 74
}

const EXEC_POSITION_ROLE_TO_STAFFER_ROLE: Record<number, number> = {
  [US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_OFFICE]: US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_OFFICE_STAFF,
  [US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_DEPARTMENT]: US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_DEPARTMENT_STAFF,
  [US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_AGENCY]: US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_AGENCY_STAFF,
  [US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_OFFICE]: US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_OFFICE_STAFF,
  [US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_DEPARTMENT]: US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_DEPARTMENT_STAFF,
  [US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_AGENCY]: US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_AGENCY_STAFF
}

export const getExecStafferRoleId = (positionGeneralRoleId: number): number | undefined =>
  EXEC_POSITION_ROLE_TO_STAFFER_ROLE[positionGeneralRoleId]

export const FEDERAL_LEADERSHIP_OFFICER_ROLES = [US_LEADERSHIP_OFFICER_ROLES.FEDERAL_OFFICER, US_LEADERSHIP_OFFICER_ROLES.FEDERAL_LEADERSHIP] as const

export const FEDERAL_EXECUTIVE_ROLES = [
  US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_OFFICE,
  US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_DEPARTMENT,
  US_ADDITIONAL_ENTITY_ROLES.FED_EXEC_AGENCY
] as const

export const STATE_EXECUTIVE_ROLES = [
  US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_OFFICE,
  US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_DEPARTMENT,
  US_ADDITIONAL_ENTITY_ROLES.STATE_EXEC_AGENCY
] as const

export const EXECUTIVE_GENERAL_ROLE_IDS = new Set<number>([
  ...FEDERAL_EXECUTIVE_ROLES,
  ...STATE_EXECUTIVE_ROLES
])

const STATE_EXECUTIVE_ROLE_IDS = new Set<number>(STATE_EXECUTIVE_ROLES)

export const isStateExecutiveRole = (roleId: number | null): boolean =>
  roleId != null && STATE_EXECUTIVE_ROLE_IDS.has(roleId)

export const filterExecutiveGeneralRoles = <T extends { id: number }>(roles: T[]): T[] =>
  roles.filter(r => EXECUTIVE_GENERAL_ROLE_IDS.has(r.id))

export interface ExecPositionStatus {
  code: 'AOK' | 'VAC'
  label: string
}

export const EXEC_POSITION_STATUSES: Record<number, ExecPositionStatus> = {
  1: { code: 'AOK', label: 'Normal' },
  2: { code: 'AOK', label: 'Acting' },
  3: { code: 'AOK', label: 'Designated' },
  4: { code: 'AOK', label: 'Nominated' },
  5: { code: 'AOK', label: 'Retiring from position' },
  6: { code: 'VAC', label: 'Deceased' },
  7: { code: 'VAC', label: 'Removed from position' },
  8: { code: 'VAC', label: 'Resigned from position' },
  9: { code: 'VAC', label: 'Retired from position' },
  10: { code: 'VAC', label: 'Unresolved' },
  11: { code: 'AOK', label: 'Term limit reached' },
  12: { code: 'AOK', label: 'Running for elected office' },
  13: { code: 'AOK', label: 'Renominated' },
  14: { code: 'VAC', label: 'Took other position' }
}

export const EXEC_POS_TYPE_ELECTED = 9

export const formatExecStatusLabel = (status: ExecPositionStatus): string =>
  `${status.code} - ${status.label}`

const STATUS_TITLE_PREFIXES = ['Acting', 'Designated', 'Nominated'] as const

export const getStatusTitleWarning = (statusId: number, title: string): string | null => {
  const status = EXEC_POSITION_STATUSES[statusId]?.label ?? ''
  const titlePrefix = STATUS_TITLE_PREFIXES.find(p => title.toLowerCase().startsWith(p.toLowerCase()))
  const statusIsAccent = (STATUS_TITLE_PREFIXES as readonly string[]).includes(status)
  if (statusIsAccent && titlePrefix !== status) {
    return `Status is "${status}" but Title does not start with "${status}".`
  }
  if (!statusIsAccent && titlePrefix != null) {
    return `Title starts with "${titlePrefix}" but Status is not set to "${titlePrefix}".`
  }
  return null
}

export const getOccupancyStatusWarning = (statusId: number, isVacant: boolean): string | null => {
  const code = EXEC_POSITION_STATUSES[statusId]?.code
  if (code == null) return null
  if (!isVacant && code === 'VAC') {
    return 'This position has an occupant; select an occupied (AOK) status before saving.'
  }
  if (isVacant && code === 'AOK') {
    return 'This position is vacant; select a vacancy (VAC) status before saving.'
  }
  return null
}

export const getStatusesForOccupancy = (
  isVacant: boolean,
  currentStatusId?: number
): Array<[number, ExecPositionStatus]> => {
  const allowed = isVacant ? 'VAC' : 'AOK'
  return Object.entries(EXEC_POSITION_STATUSES)
    .map(([id, status]): [number, ExecPositionStatus] => [Number(id), status])
    .filter(([id, status]) => status.code === allowed || id === currentStatusId)
}

export interface ExecPositionLevel { id: number, code: string }
export const EXEC_POSITION_LEVELS: Record<number, ExecPositionLevel> = {
  1: { id: 1, code: 'EX-I' },
  2: { id: 2, code: 'EX-II' },
  3: { id: 3, code: 'EX-III' },
  4: { id: 4, code: 'EX-IV' },
  5: { id: 5, code: 'EX-V' },
  6: { id: 6, code: 'SE-1' },
  7: { id: 7, code: 'SE-2' },
  8: { id: 8, code: 'GS-1' },
  9: { id: 9, code: 'GS-2' },
  10: { id: 10, code: 'GS-3' },
  11: { id: 11, code: 'GS-4' },
  12: { id: 12, code: 'GS-5' },
  13: { id: 13, code: 'GS-6' },
  14: { id: 14, code: 'GS-7' },
  15: { id: 15, code: 'GS-8' },
  16: { id: 16, code: 'GS-9' },
  17: { id: 17, code: 'GS-10' },
  18: { id: 18, code: 'GS-11' },
  19: { id: 19, code: 'GS-12' },
  20: { id: 20, code: 'GS-13' },
  21: { id: 21, code: 'GS-14' },
  22: { id: 22, code: 'GS-15' }
}

export interface ExecAppointmentPresident { id: number, name: string, years: string }
export const EXEC_APPOINTMENT_PRESIDENTS: Record<number, ExecAppointmentPresident> = {
  1: { id: 1, name: 'Lyndon B. Johnson', years: '1963 - 1969' },
  2: { id: 2, name: 'Richard M. Nixon', years: '1969 - 1974' },
  3: { id: 3, name: 'Gerald R. Ford', years: '1974 - 1977' },
  4: { id: 4, name: 'James Carter', years: '1977 - 1981' },
  5: { id: 5, name: 'Ronald Reagan', years: '1981 - 1989' },
  6: { id: 6, name: 'George H. W. Bush', years: '1989 - 1993' },
  7: { id: 7, name: 'William J. Clinton', years: '1993 - 2001' },
  8: { id: 8, name: 'George W. Bush', years: '2001 - 2009' },
  9: { id: 9, name: 'Barack Obama', years: '2009 - 2017' },
  10: { id: 10, name: 'Donald J. Trump', years: '2017 - 2021' },
  11: { id: 11, name: 'Joseph R. Biden', years: '2021 - 2024' },
  12: { id: 12, name: 'Donald J. Trump', years: '2025 - Current' }
}

export type CongressionalLiaisonCode = 0 | 1 | 2 | 3
export interface CongressionalLiaisonOption {
  id: CongressionalLiaisonCode
  label: string
}
export const CONGRESSIONAL_LIAISON_OPTIONS: CongressionalLiaisonOption[] = [
  { id: 0, label: 'Not A Liaison' },
  { id: 1, label: 'Both Chambers' },
  { id: 2, label: 'US House Only' },
  { id: 3, label: 'US Senate Only' }
]

export const CCO_OPTIONS = [
  { id: 0, label: 'No' },
  { id: 1, label: 'Yes' }
] as const

export const CHAMBERS: Record<string, Chamber> = {
  US_HOUSE: { id: 1, code: 'FH', desc: 'US House' },
  US_SENATE: { id: 2, code: 'FS', desc: 'US Senate' },
  STATE_HOUSE: { id: 3, code: 'SH', desc: 'State House' },
  STATE_SENATE: { id: 4, code: 'SS', desc: 'State Senate' },
  US_JOINT: { id: 5, code: 'FJ', desc: 'US Joint' },
  STATE_JOINT: { id: 6, code: 'SJ', desc: 'State Joint' }
} as const

export const FEDERAL_CHAMBERS = [CHAMBERS.US_HOUSE, CHAMBERS.US_SENATE, CHAMBERS.US_JOINT] as const
export const STATE_CHAMBERS = [CHAMBERS.STATE_HOUSE, CHAMBERS.STATE_SENATE, CHAMBERS.STATE_JOINT] as const
export const HOUSE_CHAMBERS = [CHAMBERS.US_HOUSE, CHAMBERS.STATE_HOUSE] as const
export const JOINT_CHAMBERS = [CHAMBERS.US_JOINT, CHAMBERS.STATE_JOINT] as const

export enum ElectCode {
  'G' = 'General',
  'P' = 'Primary',
  'S' = 'Special',
  'A' = 'Appointed'
}

const GENERAL_ELECT_CODE = 'G'
const APPOINTED_ELECT_CODE = 'A'
const FIRST_ELECT_MIN_YEAR = 1900
const NEXT_ELECT_MIN_YEAR = 2000
const TERM_NUMBER_MAX = 99
const LAST_VOTE_MIN = 0
const LAST_VOTE_MAX = 100
const YEAR_PATTERN = /^[0-9]{4}$/
const MONTH_YEAR_PATTERN = /^([0-9]{1,2})\/([0-9]{4})$/
const MONTH_DAY_YEAR_PATTERN = /^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/

type ExecPositionElectionFields = Pick<ExecutivePosition, 'typeId' | 'electDate' | 'electCode' | 'percentVote' | 'reelectYear' | 'termNumber'>
export type ExecPositionFieldErrors = Partial<Record<Exclude<keyof ExecPositionElectionFields, 'typeId'>, string>>

const trimmedText = (value: string | null): string => (value ?? '').trim()

const isYearFrom = (value: string, minYear: number): boolean =>
  YEAR_PATTERN.test(value) && Number(value) >= minYear

const isRealDateFrom = (year: number, month: number, day: number): boolean => {
  if (year < FIRST_ELECT_MIN_YEAR) return false
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

const isFirstElectDate = (value: string): boolean => {
  if (isYearFrom(value, FIRST_ELECT_MIN_YEAR)) return true
  const monthYear = MONTH_YEAR_PATTERN.exec(value)
  if (monthYear != null) return isRealDateFrom(Number(monthYear[2]), Number(monthYear[1]), 1)
  const monthDayYear = MONTH_DAY_YEAR_PATTERN.exec(value)
  return monthDayYear != null && isRealDateFrom(Number(monthDayYear[3]), Number(monthDayYear[1]), Number(monthDayYear[2]))
}

const isElectCode = (code: string): boolean => Object.keys(ElectCode).includes(code)

const electCodeError = (code: string): string | undefined => {
  if (code === '') return 'Elect Type is required for an elected position.'
  if (!isElectCode(code)) return 'Elect Type must be G, P, S or A.'
}

const firstElectError = (code: string, firstElect: string): string | undefined => {
  if (!isElectCode(code) || firstElect === '') return undefined
  if (code === GENERAL_ELECT_CODE) {
    return isYearFrom(firstElect, FIRST_ELECT_MIN_YEAR) ? undefined : 'First Elect must be a 4-digit year (1900 or later) when Elect Type is General.'
  }
  return isFirstElectDate(firstElect) ? undefined : 'First Elect must be YYYY, M/YYYY or M/D/YYYY (a real date, 1900 or later).'
}

const nextElectError = (nextElect: string): string | undefined => {
  if (nextElect === '') return 'Next Elect is required for an elected position.'
  if (!isYearFrom(nextElect, NEXT_ELECT_MIN_YEAR)) return 'Next Elect must be a 4-digit year (2000 or later).'
}

const termNumberError = (termNumber: number | null): string | undefined => {
  if (termNumber == null || termNumber === 0) return 'Term Num is required for an elected position (1 or more).'
  if (!Number.isInteger(termNumber) || termNumber < 1 || termNumber > TERM_NUMBER_MAX) return 'Term Num must be a whole number from 1 to 99.'
}

const lastVoteError = (code: string, percentVote: number | null): string | undefined => {
  if (percentVote == null || code === APPOINTED_ELECT_CODE) return undefined
  if (!(percentVote >= LAST_VOTE_MIN && percentVote <= LAST_VOTE_MAX)) return 'Last Vote must be between 0 and 100.'
}

export const getExecPositionFieldErrors = (position: ExecPositionElectionFields, isVacant: boolean): ExecPositionFieldErrors => {
  if (position.typeId !== EXEC_POS_TYPE_ELECTED || isVacant) return {}
  const code = trimmedText(position.electCode)
  const errors: ExecPositionFieldErrors = {
    electCode: electCodeError(code),
    electDate: firstElectError(code, trimmedText(position.electDate)),
    reelectYear: nextElectError(trimmedText(position.reelectYear)),
    termNumber: termNumberError(position.termNumber),
    percentVote: lastVoteError(code, position.percentVote)
  }
  return Object.fromEntries(Object.entries(errors).filter(([, message]) => message != null))
}

export enum MajMinAffilication {
  'MIN' = 'Minority',
  'MAJ' = 'Majority',
  'OTH' = 'Other'
}

export * from './usernameMapping'
